export type ShareChannel = 'twitter' | 'linkedin' | 'telegram' | 'copy' | 'native';

export interface SharePayload {
  url?: string;
  title?: string;
  text?: string;
}

function resolveUrl(payload: SharePayload): string {
  return payload.url ?? (typeof window !== 'undefined' ? window.location.href : '');
}

export function getShareUrl(
  channel: Exclude<ShareChannel, 'copy' | 'native'>,
  payload: SharePayload,
): string {
  const url = encodeURIComponent(resolveUrl(payload));
  const text = encodeURIComponent(payload.text ?? payload.title ?? '');

  switch (channel) {
    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${url}${text ? `&text=${text}` : ''}`;
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    case 'telegram':
      return `https://t.me/share/url?url=${url}${text ? `&text=${text}` : ''}`;
  }
}

export function openShareWindow(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer,width=600,height=520');
}

export async function copyShareLink(url?: string): Promise<boolean> {
  const target = url ?? (typeof window !== 'undefined' ? window.location.href : '');
  if (!target) return false;

  try {
    await navigator.clipboard.writeText(target);
    return true;
  } catch {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = target;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

export type NativeShareResult = 'shared' | 'aborted' | 'unsupported';

export async function nativeShare(payload: SharePayload): Promise<NativeShareResult> {
  if (typeof navigator === 'undefined' || !navigator.share) {
    return 'unsupported';
  }

  const url = resolveUrl(payload);
  const data: ShareData = { url };
  if (payload.title) data.title = payload.title;
  if (payload.text) data.text = payload.text;

  try {
    if (navigator.canShare && !navigator.canShare(data)) {
      return 'unsupported';
    }
    await navigator.share(data);
    return 'shared';
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return 'aborted';
    }
    return 'unsupported';
  }
}

export function canUseNativeShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}
